/**
 * PodsPage — dedicated page for viewing pod members' collections and shared activity.
 *
 * Replaces the old ViewSwitcher dropdown. Your main tabs (This Year, History)
 * always show YOUR data. This page is where you see what friends are up to.
 *
 * Sections:
 *   1. Pod tabs (if multi-pod)
 *   2. "Together" — items all members have engaged with
 *   3. Per-member rows — each member's recent items
 *   4. Recent activity feed
 *
 * Props:
 *   items — Collection items with _userData attached (from adapter fetch)
 *   collectionType — 'videogames' | 'movies' | 'books' | 'boardgames'
 *   getItemImage — (item) => url string or null
 *   getItemSubtitle — (item) => string (e.g. year, author)
 *   getItemStatus — (item, userName) => string or null
 *   notEngagedStatuses — statuses that don't count as engagement (e.g. ['Not Started'])
 *   pocketbaseURL — for building image URLs if needed
 */

import React, { useMemo, useCallback, useState } from 'react';
import { usePod } from '../hooks/usePod.jsx';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';
import { userHasEngaged, filterPodItems } from '../hooks/useUserView.jsx';

function MiniCard({ item, getImage, getSubtitle, date, onClick }) {
    const imageUrl = getImage?.(item);
    const subtitle = getSubtitle?.(item);
    return (
        <div
            role="button"
            tabIndex={0}
            className="flex-shrink-0 w-[180px] sm:w-[200px] bg-surface rounded-lg overflow-hidden group cursor-pointer hover:bg-surface-hover hover:-translate-y-0.5 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
            onClick={() => onClick?.(item)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(item); } }}
            aria-label={item.title}
        >
            <div className="w-full aspect-video bg-bg-tertiary relative overflow-hidden">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-tertiary text-xs">
                        No image
                    </div>
                )}
            </div>
            <div className="px-3 py-2.5 space-y-0.5">
                <p className="text-sm font-medium text-text-primary truncate">{item.title}</p>
                {subtitle && (
                    <p className="text-xs text-text-tertiary truncate">{subtitle}</p>
                )}
                {date && (
                    <p className="text-xs text-text-tertiary">{formatRelativeDate(date)}</p>
                )}
            </div>
        </div>
    );
}

function HorizontalCardRow({ items, getImage, getSubtitle, dateField, memberName, onItemClick }) {
    if (items.length === 0) {
        return (
            <p className="text-sm text-text-tertiary italic py-4">Nothing yet</p>
        );
    }

    return (
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-border-subtle scrollbar-track-transparent">
            {items.slice(0, 10).map((item) => {
                // Get the most recent date across all members, or a specific member's date
                let date = null;
                if (dateField && item._userData) {
                    if (memberName) {
                        date = item._userData[memberName]?.[dateField];
                    } else {
                        for (const ud of Object.values(item._userData)) {
                            const d = ud[dateField];
                            if (d && (!date || d > date)) date = d;
                        }
                    }
                }
                return (
                    <MiniCard
                        key={item.id}
                        item={item}
                        getImage={getImage}
                        getSubtitle={getSubtitle}
                        date={date}
                        onClick={onItemClick}
                    />
                );
            })}
            {items.length > 10 && (
                <div className="flex-shrink-0 w-[180px] sm:w-[200px] flex items-center justify-center text-sm text-text-tertiary">
                    +{items.length - 10} more
                </div>
            )}
        </div>
    );
}

function ActivityEntry({ emoji, action, title, imageUrl, date, together, onTitleClick }) {
    return (
        <div className="flex items-center gap-3 py-2">
            {imageUrl ? (
                <img src={imageUrl} alt={title} className="w-10 h-10 rounded object-cover flex-shrink-0" loading="lazy" />
            ) : (
                <div className="w-10 h-10 rounded bg-bg-tertiary flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <p className="text-sm text-text-secondary truncate">
                    <span>{emoji}</span>
                    {' '}{action}{' '}
                    <button onClick={onTitleClick} className="font-semibold text-text-primary hover:underline">
                        {title}
                    </button>
                    {together && <span className="text-text-tertiary"> together</span>}
                </p>
            </div>
            <span className="text-xs text-text-tertiary flex-shrink-0">{date}</span>
        </div>
    );
}

function formatRelativeDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function PodsPage({
    items = [],
    collectionType = 'movies',
    getItemImage,
    getItemSubtitle,
    getItemStatus,
    notEngagedStatuses = [],
    onItemClick,
}) {
    const currentUser = useCurrentUser();
    const { pods } = usePod();

    // Local pod selection — tab between pods on this page only
    const [activePodIdx, setActivePodIdx] = useState(0);
    const activePodData = pods[activePodIdx] || pods[0];
    const pod = activePodData?.pod || null;
    const members = activePodData?.members || [];

    // Date field used for sorting, per collection type
    const dateField = collectionType === 'videogames' ? 'lastPlayed'
        : collectionType === 'boardgames' ? 'firstPlayed'
        : collectionType === 'books' ? 'dateRead'
        : 'watchedDate';

    // Sort items by date (most recent first) using a specific member's date, or earliest across all members
    const sortByDate = useCallback((itemsToSort, memberName) => {
        return [...itemsToSort].sort((a, b) => {
            const aDate = memberName
                ? a._userData?.[memberName]?.[dateField]
                : Object.values(a._userData || {}).reduce((best, ud) => {
                    const d = ud[dateField];
                    return d && (!best || d > best) ? d : best;
                }, null);
            const bDate = memberName
                ? b._userData?.[memberName]?.[dateField]
                : Object.values(b._userData || {}).reduce((best, ud) => {
                    const d = ud[dateField];
                    return d && (!best || d > best) ? d : best;
                }, null);
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1;
            if (!bDate) return -1;
            return bDate.localeCompare(aDate);
        });
    }, [dateField]);

    // Together items — items where enough pod members have engaged.
    // A member counts as "engaged" if they have a personal watch/play record
    // OR if they appear as a co-participant in an activity (_coParticipantIds).
    // This handles the case where only one person logged the item individually
    // but recorded watching it together via the activity system.
    const togetherItems = useMemo(() => {
        if (!pod || members.length < 2) return [];

        const threshold = pod.togetherThreshold && pod.togetherThreshold > 0
            ? pod.togetherThreshold
            : members.length;

        const filtered = items.filter(item => {
            const coPartIds = new Set(item._coParticipantIds || []);
            const engagedCount = members.filter(m =>
                userHasEngaged(item, m.userName, collectionType, notEngagedStatuses)
                || coPartIds.has(m.userId)
            ).length;
            return engagedCount >= threshold;
        });

        return sortByDate(filtered, null).slice(0, 20);
    }, [items, pod, members, collectionType, notEngagedStatuses, sortByDate]);

    // Per-member items (excluding current user, excluding together items)
    const memberSections = useMemo(() => {
        if (!pod || !currentUser) return [];
        const togetherIds = new Set(togetherItems.map(i => i.id));
        const otherMembers = members.filter(m => m.userId !== currentUser.id);
        return otherMembers.map(member => {
            const memberItems = items.filter(item => {
                if (togetherIds.has(item.id)) return false;
                return userHasEngaged(item, member.userName, collectionType, notEngagedStatuses);
            });
            return {
                member,
                items: sortByDate(memberItems, member.userName).slice(0, 20),
            };
        });
    }, [items, pod, members, currentUser, togetherItems, sortByDate]);

    // Activity feed — derive from _userData date fields, most recent first
    const activityFeed = useMemo(() => {
        if (!pod || members.length === 0) return [];
        const activities = [];

        const statusField = collectionType === 'videogames' ? 'status'
            : collectionType === 'books' ? 'status'
            : 'status';

        for (const item of items) {
            if (!item._userData) continue;
            for (const member of members) {
                const ud = item._userData[member.userName];
                if (!ud) continue;
                const dateVal = ud[dateField];
                if (!dateVal) continue;

                const status = ud[statusField];
                let action;
                if (status === 'Watching' || status === 'Playing' || status === 'Reading') {
                    action = collectionType === 'videogames' ? 'playing'
                        : collectionType === 'books' ? 'reading'
                        : 'watching';
                } else if (status === 'Watched' || status === 'Completed' || status === 'Read') {
                    action = collectionType === 'videogames' ? 'finished'
                        : collectionType === 'books' ? 'finished'
                        : 'watched';
                } else {
                    action = collectionType === 'videogames' ? 'played'
                        : collectionType === 'books' ? 'read'
                        : collectionType === 'boardgames' ? 'played'
                        : 'watched';
                }

                // Check if this was a "together" activity
                const otherMembers = members.filter(m => m.id !== member.id);
                const allEngaged = otherMembers.every(m => {
                    const oud = item._userData[m.userName];
                    return oud && oud[dateField];
                });
                const coParticipantIds = item._coParticipantIds || [];
                const memberUserIds = new Set(members.map(m => m.userId));
                const hasTogetherActivity = coParticipantIds.some(id => memberUserIds.has(id));

                if ((allEngaged || hasTogetherActivity) && members.length > 1) {
                    // Only add together entry once (from first member alphabetically)
                    const firstMember = [...members].sort((a, b) => a.userName.localeCompare(b.userName))[0];
                    if (member.id === firstMember.id) {
                        activities.push({
                            key: `${item.id}-together`,
                            emoji: members.map(m => m.emoji).join(''),
                            action,
                            item,
                            together: true,
                            date: dateVal,
                            sortDate: new Date(dateVal),
                        });
                    }
                } else {
                    activities.push({
                        key: `${item.id}-${member.userId}`,
                        emoji: member.emoji,
                        action,
                        item,
                        together: false,
                        date: dateVal,
                        sortDate: new Date(dateVal),
                    });
                }
            }
        }

        // Sort by date descending, take top 20
        activities.sort((a, b) => b.sortDate - a.sortDate);
        return activities.slice(0, 20);
    }, [items, pod, members, collectionType]);

    // No pod state
    if (!pod) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center py-16">
                    <h2 className="text-2xl font-bold font-display text-text-primary mb-3">Pods</h2>
                    <p className="text-text-secondary mb-6">
                        You're tracking solo right now. Create or join a pod to share with friends.
                    </p>
                    <p className="text-sm text-text-tertiary">
                        Head to Settings to create or join a pod.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            {/* Pod tabs — only shown when user belongs to multiple pods */}
            {pods.length > 1 && (
                <div className="flex gap-2 mb-8">
                    {pods.map((p, idx) => (
                        <button
                            key={p.pod?.id}
                            onClick={() => setActivePodIdx(idx)}
                            className={[
                                'px-4 py-2 rounded-full text-sm font-medium transition-colors',
                                activePodIdx === idx
                                    ? 'bg-accent-primary text-text-inverse'
                                    : 'bg-surface border border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-strong',
                            ].join(' ')}
                        >
                            {p.pod?.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Pod name */}
            <div className="flex items-baseline gap-3 mb-8">
                <h1 className="text-3xl font-bold font-display text-text-primary">{pod.name}</h1>
                <span className="text-sm text-text-tertiary">
                    {members.length} member{members.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Together section */}
            {members.length >= 2 && (
                <section className="mb-10">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-xl">
                            {members.map(m => m.emoji).join('')}
                        </span>
                        <h2 className="text-lg font-semibold font-display text-text-primary">
                            {collectionType === 'videogames' ? 'Played Together'
                                : collectionType === 'books' ? 'Read Together'
                                : collectionType === 'boardgames' ? 'Played Together'
                                : 'Watched Together'}
                        </h2>
                        <span className="text-sm text-text-tertiary">
                            {togetherItems.length} {togetherItems.length === 1 ? 'item' : 'items'}
                        </span>
                    </div>
                    <HorizontalCardRow
                        items={togetherItems}
                        getImage={getItemImage}
                        getSubtitle={getItemSubtitle}
                        dateField={dateField}
                        onItemClick={onItemClick}
                    />
                </section>
            )}

            {/* Per-member sections */}
            {memberSections.map(({ member, items: memberItems }) => (
                <section key={member.id} className="mb-10">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-xl">{member.emoji}</span>
                        <h2 className="text-lg font-semibold font-display text-text-primary">
                            {member.displayName}'s {
                                collectionType === 'videogames' ? 'Playing'
                                : collectionType === 'books' ? 'Reading'
                                : collectionType === 'boardgames' ? 'Playing'
                                : 'Watching'
                            }
                        </h2>
                        <span className="text-sm text-text-tertiary">
                            {memberItems.length} {memberItems.length === 1 ? 'item' : 'items'}
                        </span>
                    </div>
                    <HorizontalCardRow
                        items={memberItems}
                        getImage={getItemImage}
                        getSubtitle={getItemSubtitle}
                        dateField={dateField}
                        memberName={member.userName}
                        onItemClick={onItemClick}
                    />
                </section>
            ))}

            {/* Divider */}
            {activityFeed.length > 0 && (
                <div className="border-t border-border-subtle my-8" />
            )}

            {/* Activity feed */}
            {activityFeed.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold font-display text-text-primary mb-4">
                        Recent Activity
                    </h2>
                    <div className="space-y-1">
                        {activityFeed.map(act => (
                            <ActivityEntry
                                key={act.key}
                                emoji={act.emoji}
                                action={act.action}
                                title={act.item.title}
                                imageUrl={getItemImage?.(act.item)}
                                date={formatRelativeDate(act.date)}
                                together={act.together}
                                onTitleClick={() => onItemClick?.(act.item)}
                            />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
